"use client";
import clsx from 'clsx';
import { AdbeatButton } from '../components/adbeatButton';
import { avenirNextDemi } from '@/styles/fonts';
import { useActionState, useEffect, useRef } from 'react';
import { logError } from '@/app/lib/logger';
import { useState } from 'react';
import TurnstileWidget, { type TurnstileWidgetHandle } from '@/app/components/TurnstileWidget.client';

const labelClassName = clsx(
    avenirNextDemi.className,
    "text-[#c0cad1]",
    "text-[12px]",
    "font-normal",
    "uppercase",
);

const inputClassName = clsx(
    "h-[46px]",
    avenirNextDemi.className,
    "text-[15px]",
    "text-[#999]",
    "w-full",
    "appearance-none",
    "bg-white",
    "border",
    "border-[#f2f5f9]",
    "rounded-[5px]",
    "px-[15px]",
    "box-border",
    "shadow-[inset_0_0px_1px_#f2f5f9]",
);

type FormState = {
    state: 'idle' | 'loading' | 'success' | 'error';
    emailErrors?: string[];
    message?: string;
};

const initialState: FormState = {
    state: 'idle',
    emailErrors: [],
    message: "",
};

export default function ResetForm() {
    const [turnstileToken, setTurnstileToken] = useState('');
    const turnstileRef = useRef<TurnstileWidgetHandle>(null);
    const [formState, handleForgotPassword] = useActionState<FormState>(
        async (initialState: FormState, formData?: FormData): Promise<FormState> => {
            const email = formData?.get('email') as string;
            if (!email) {
                return {
                    emailErrors: ["Email is required."],
                    state: 'error',
                };
            }

            if (!turnstileToken) {
                return {
                    message: 'Please complete the verification challenge.',
                    state: 'error',
                };
            }

            formData?.set('cf-turnstile-response', turnstileToken);

            try {
                const response = await fetch('/api/forgot_password/reset', {
                    method: 'POST',
                    body: formData,
                });

                const result = await response.json();

                if (response.ok && result.success) {
                    return {
                        message: result.message,
                        state: 'success',
                    };
                } else {
                    return {
                        message: result.error || "An unexpected error occurred.",
                        state: 'error',
                    };
                }
            } catch (error) {
                logError(error, { context: 'Error submitting forgot password form' });
                return {
                    message: "An unexpected error occurred. Please try again later.",
                    state: 'error',
                };
            }
        },
        initialState
    );

    // Turnstile tokens are single-use. After any failed reset attempt, the
    // previous token has already been consumed. Reset the widget so the user
    // can re-solve before resubmitting.
    useEffect(() => {
        if (formState.state === 'error') {
            turnstileRef.current?.reset();
            setTurnstileToken('');
        }
    }, [formState]);

    return (
        <>
            {formState.state === 'success' && (
                <div className={clsx(
                    "text-center",
                    "text-[#86939e]",
                    "mb-[10px]",
                )}>
                    {formState.message}
                </div>
            )}

            {formState.state === 'error' && (
                <div className={clsx(
                    "text-center",
                    "text-red-500",
                    "mb-[10px]",
                )}>
                    {formState.message}
                </div>
            )}

            {formState.state !== 'success' && (
                <>
                    <div
                        className={clsx(
                            "mb-[10px]",
                            "text-[#86939e]",
                            "text-[14px]",
                            "text-center",
                        )}
                    >
                        Enter your email, and we&apos;ll send you instructions on how to reset your password.
                    </div>
                    <form action={handleForgotPassword}>
                        <div className="mb-[10px]">
                            <label className={labelClassName}>Email</label>
                            <input type="text"
                                defaultValue=""
                                className={inputClassName}
                                name="email" placeholder="Email" />
                            <div className={clsx(
                                "text-red-500",
                                "text-[12px]",
                                "mt-[5px]",
                            )}>
                                {formState?.emailErrors?.map((error, index) => (
                                    <div key={index}>{error}</div>
                                ))}
                            </div>
                        </div>
                        <div className="mb-[10px]">
                            <TurnstileWidget
                                ref={turnstileRef}
                                action="forgot_password"
                                onTokenChange={setTurnstileToken}
                            />
                        </div>
                        <AdbeatButton
                            type="submit"
                            text="Reset Password"
                            name='reset_password'
                            className={clsx(
                                "sm:w-full",
                                'bg-gradient-to-b',
                                'border-[#2da1f8]',
                                'from-[#2da1f8]',
                                'to-[#1991ed]',
                                'text-[16px]',
                                'min-w-[192px]',
                                "mx-auto",
                                avenirNextDemi.className,
                            )} />
                    </form>
                </>
            )}
        </>);
}

